import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginFormData, AuthError } from '../types/auth';
import { getAuthService, IAuthService } from '../services';

interface UseLoginFormReturn {
  formData: LoginFormData;
  isLoading: boolean;
  error: AuthError | null;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
}

const INITIAL_FORM_STATE: LoginFormData = {
  email: '',
  password: '',
  rememberMe: false
};

export const useLoginForm = (): UseLoginFormReturn => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);
  const [formData, setFormData] = useState<LoginFormData>(INITIAL_FORM_STATE);
  const [authService, setAuthService] = useState<IAuthService | null>(null);

  useEffect(() => {
    let mounted = true;

    const initService = async () => {
      try {
        const service = await getAuthService();
        if (mounted) {
          setAuthService(service);
        }
      } catch (error) {
        console.error('Failed to initialize auth service:', error);
        if (mounted) {
          setError({ message: 'Failed to initialize authentication service' });
        }
      }
    };

    initService();

    return () => {
      mounted = false;
    };
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!authService) {
      setError({ message: 'Authentication service not initialized' });
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await authService.signIn(formData.email, formData.password);
      await login(response.token, response.user, formData.rememberMe);
      navigate('/Projects');
    } catch (err) {
      if (err instanceof Error) {
        setError({ 
          message: err.message, 
          code: 'code' in err ? (err as any).code : undefined 
        });
      } else {
        setError({ message: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [formData, login, navigate, authService]);

  return {
    formData,
    isLoading,
    error,
    handleInputChange,
    handleSubmit
  };
}; 